import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { getPrescriptions } from '../../../../apis/prescription'
import { getAdditionalPresc } from '../../../../apis/getAdditionalPresc'
import { getUniquePrescriptions } from '../../../../helpers/getUniquePrescriptions'
import { GlobalState } from '../../../../stores/reducer'
import { Prescription } from '../../../../types/models/Presc'
import { queryKeys } from '../../../../shared'

/**
 * 処方箋リストを取得・ポーリングする Query hook
 *
 * 初回は全件取得、2回目以降は追加分のみを取得してマージする。
 * 2秒間隔でポーリングし、リアルタイム性を確保する。
 */
export const usePrescriptionsQuery = () => {
  const queryClient = useQueryClient()
  const isLoggedIn = useSelector((state: GlobalState) => state.isLoggedIn)

  return useQuery({
    queryKey: queryKeys.prescriptions.list(),
    queryFn: async (): Promise<Prescription[]> => {
      // 現在のキャッシュデータを取得
      const cachedData = queryClient.getQueryData<Prescription[]>(
        queryKeys.prescriptions.list()
      )

      if (!cachedData || cachedData.length === 0) {
        // 初回取得: 全件取得
        const { data } = await getPrescriptions()
        console.log('presc_from_back', data)
        return data
      }

      // 追加取得: 最新のタイムスタンプ以降のデータを取得
      const latestTimestamp = cachedData[0].request_event_created_at
      const { data: additionalPresc } = await getAdditionalPresc(
        latestTimestamp
      )

      if (additionalPresc.length === 0) {
        // 追加データがなければキャッシュをそのまま返す
        return cachedData
      }

      // 追加データとキャッシュをマージして重複排除
      const mergedPrescriptions = [...additionalPresc, ...cachedData]
      return getUniquePrescriptions(mergedPrescriptions)
    },
    enabled: isLoggedIn,
    refetchInterval: 2000, // 2秒ポーリング
    staleTime: 0, // 常に最新データを要求
    refetchOnMount: true,
  })
}

/**
 * 新規処方箋が追加されたかを検知するためのヘルパー
 */
export const useHasNewPrescriptions = () => {
  const { data: prescriptions, dataUpdatedAt } = usePrescriptionsQuery()
  const queryClient = useQueryClient()

  // 前回のデータ件数を比較して新規追加を検知
  const previousCount =
    queryClient.getQueryData<Prescription[]>(queryKeys.prescriptions.list())
      ?.length ?? 0
  const currentCount = prescriptions?.length ?? 0

  return {
    hasNew: currentCount > previousCount && previousCount > 0,
    dataUpdatedAt,
  }
}
